/**
 * Platform Status Banner
 *
 * Purpose: Renders at most one global banner above the whole app — a
 *          non-dismissible outage banner when the backend looks unreachable,
 *          otherwise a dismissible "something went wrong" banner when an
 *          uncaught JS error was latched. The two are mutually exclusive;
 *          outage takes priority. See ORCH-285.
 */

import React from 'react';
import { Alert, Button, Collapse } from '@mui/material';
import { DismissibleBanner } from './DismissibleBanner';
import { useConnectivity } from '../../context/ConnectivityContext';

export const PlatformStatusBanner: React.FC = () => {
  const { networkStatus, appError, checkNow, clearAppError } = useConnectivity();

  if (networkStatus === 'outage') {
    return (
      <Collapse in>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={checkNow}>
              Retry now
            </Button>
          }
        >
          We&apos;re having trouble connecting to the server. Some features may not work until this
          is resolved.
        </Alert>
      </Collapse>
    );
  }

  if (appError) {
    return (
      <DismissibleBanner severity="warning" onDismiss={clearAppError}>
        Something went wrong. Try refreshing the page.
      </DismissibleBanner>
    );
  }

  return null;
};

export default PlatformStatusBanner;
